import { useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter, NavLink } from "react-router-dom";

import { PageTitle } from "../../components/Common/PageTitle";
import { InfiniteScroller } from "../../components/InfiniteScroller";
import { MediaCatalogueItemCard } from "../../components/MediaCatalogueComponents/MediaCatalogueItemCard";
import { ModalUploadMediaCatalogue } from "../../components/MediaCatalogueComponents/ModalUploadMediaCatalogue";
import { useMe } from "../../hooks/queries";
import { AccountType } from "../../models/AccountData";
import { FeatureUseLimitCalculator } from "../../models/featureUseLimits/FeatureUseLimitsCalculator";
import { FeatureUseStatus, OrgFeatures } from "../../models/featureUseLimits/FeatureUseLimitsData.const";
import { MediaCatalogueItem } from "../../models/MediaCatalogueItem";
import DataSvc from "../../services/dataSvc";
import useLocation from "../FacilityDetailsPage/hooks/useLocation";

import "./styles.scss";

interface MatchParams {
  locationId?: string;
}

type IMediaCataloguePageProps = RouteComponentProps<MatchParams>;

const MediaCataloguePage: React.FunctionComponent<IMediaCataloguePageProps> = (
  props
) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`mediaCataloguePage.${key}`);

  const queryClient = useQueryClient();

  const locationId = props.match.params.locationId ?? "";

  const { data: locationData } = useLocation(locationId);
  const { data: me } = useMe();

  const [showUploadModal, setShowUploadModal] = useState(false);

  const canUpload =
    me?.type === AccountType.ADMIN ||
    me?.type === AccountType.LOC_ADMIN ||
    (me?.type === AccountType.ORG_ADMIN &&
      FeatureUseLimitCalculator.getFeatureUseStatus(
        OrgFeatures.USE_LOCAL_POST,
        me.organization?.commonFeatureUseLimit
      ) === FeatureUseStatus.CAN_USE);

  const refreshScrollerRef = React.useRef<(() => void) | null>(null);

  const handleUploadSuccess = () => {
    refreshScrollerRef.current?.();
    queryClient.invalidateQueries(["location.mediaCatalogue", locationId]);
  };

  const handleDeleteItem = async (item: MediaCatalogueItem) => {
    try {
      await DataSvc.deleteMediaCatalogueItem(locationId, item.id);
      refreshScrollerRef.current?.();
      queryClient.invalidateQueries(["location.mediaCatalogue", locationId]);
    } catch {
      // Axios handles displaying the error automatically.
    }
  };

  const fetchCatalogueItems = useCallback(async (params: any) => {
    const result = await DataSvc.getMediaCatalogue(locationId, params);
    const items: MediaCatalogueItem[] = Array.isArray(result?.items) ? result.items : [];
    return { data: items, count: result?.total ?? 0 };
  }, [locationId]);

  return (
    <div className="right-content media-catalogue-page">
      <div className="back-top-bar">
        <NavLink to={`/location/${locationId}`} className="back-link">
          <i className="icons icon-back"></i>
          {t("back_to_facility_details")}
        </NavLink>
      </div>

      {locationData && (
        <>
          <div className="location-name">
            <PageTitle text={locationData.name} />
            {canUpload && (
              <button
                className="btn btn-blue btn-upload-catalogue"
                onClick={() => setShowUploadModal(true)}
              >
                <Upload size={16} color="white" className="upload-icon" />
                {t("upload")}
              </button>
            )}
          </div>

          <div className="catalogue-container">
            <InfiniteScroller
              direction="horizontal"
              pageSize={10}
              dependencyKey={`${locationId}`}
              fetchFunction={fetchCatalogueItems}
              initialParams={{
                order: "desc",
                sort: "created_at",
              }}
              noItemsComponent={<div>{t("no_items")}</div>}
              onRefresh={(fn) => {
                refreshScrollerRef.current = fn;
              }}
              renderItem={(item: any) => (
                <MediaCatalogueItemCard
                  key={item.id}
                  item={item as MediaCatalogueItem}
                  onDelete={canUpload ? handleDeleteItem : undefined}
                />
              )}
            />
          </div>
        </>
      )}

      {showUploadModal && (
        <ModalUploadMediaCatalogue
          locationId={locationId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};

export default withRouter(MediaCataloguePage);
